import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  Dispatch,
  SetStateAction,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {api, barataoApis} from '../services/api';
import {database} from '../database';
import {User as ModelUser} from '../database/models/User';
import type {AxiosError, AxiosInstance} from 'axios';

import AsyncStorage from '@react-native-async-storage/async-storage';
import JSEncrypt from 'jsencrypt';
import Config from 'react-native-config';
import {Platform} from 'react-native';
import {useCards} from './cards';
import {useCarrinhos} from './useCarrinhos';

import {getMessaging, getToken} from '@react-native-firebase/messaging';
import {identifyUserForPush} from '../lib/pushMessaging';
import {rastrear, setRastrearClientId, setRastrearLogado} from '../services/rastrear';
import {setShouldShowOTPModal} from './otpModal';

interface User {
  id: string;
  user_id: string;
  email: string;
  name: string;
  cpf: string;
  phone: string;
  avatarUrl: string;
  token: string;
  codCompartilhar: string;
  phone_verified: boolean;
}

interface SignInCredentials {
  token: string;
  user: User;
}

interface AuthContextData {
  user: User;
  isLogged: boolean;
  showSign: any;
  defaultAvatar: string;
  sessionExpired: boolean;
  markPhoneAsVerified: () => Promise<void>;
  signIn: (credentials: SignInCredentials) => Promise<void>;
  signOut: () => Promise<void>;
  stringToHex: (str: string) => string | false;
  updateAvatar: (user: User) => Promise<void>;
  setShowSign: Dispatch<SetStateAction<any>>;
  openSign: (statusTela?: string) => void;
  signStatusTela: string;
  setShouldShowOTPModal: (next: boolean) => void;
  setSessionExpired: Dispatch<SetStateAction<boolean>>;
  signDismissable: boolean;
  setSignDismissable: Dispatch<SetStateAction<boolean>>;
  fetchUserData: (
    token?: string,
    incentiveUserId?: string | null,
    clientId?: string,
  ) => Promise<void>;
  clientId?: string | null;
  setClientId: Dispatch<SetStateAction<string | null>>;
  incentiveUserId?: string | null;
  isEmprestimoEligible: boolean;
  isAuthReady: boolean;
  setIsEmprestimoEligible: React.Dispatch<React.SetStateAction<boolean>>;
  forgotPasswordFromChangePassword: boolean;
  setForgotPasswordFromChangePassword: Dispatch<SetStateAction<boolean>>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const publicKeyRSA = Config.RN_AUTH_PUBLIC_KEY_RSA as string;

const defaultAvatar =
  'https://sbcf.fr/wp-content/uploads/2018/03/sbcf-default-avatar.png';

const INITIAL_USER = {
  id: '',
  user_id: 'convidado',
  name: 'Convidado',
  cpf: '',
  email: '',
  phone: '',
  avatarUrl: defaultAvatar,
  token: '',
  codCompartilhar: '',
  phone_verified: false,
};

function AuthProvider({children}: AuthProviderProps) {
  const {handleClearUserCardsList} = useCards();
  const {resetCarrinhos} = useCarrinhos();
  const [data, setData] = useState<User>(INITIAL_USER as User);
  const [showSign, setShowSign] = useState(false);
  const [signStatusTela, setSignStatusTela] = useState('loginHome');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [signDismissable, setSignDismissable] = useState(true);

  const openSign = (statusTela = 'loginHome') => {
    setSignStatusTela(statusTela);
    setShowSign(true);
  };

  const [isLogged, setIsLogged] = useState(false);
  const [isEmprestimoEligible, setIsEmprestimoEligible] = useState(false);
  const [forgotPasswordFromChangePassword, setForgotPasswordFromChangePassword] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  // API para mostrar modal a cada X dias
  // const hasCheckedPhoneVerificationRef = useRef(false);

  // Debug: track when auth state flips to true
  useEffect(() => {
    if (isLogged) {
      console.log('[Auth] isLogged = true');
    }
  }, [isLogged]);

  // Check loan eligibility globally when user logs in


  const signingOutRef = useRef(false);
  const isLoggedRef = useRef(isLogged);

  useEffect(() => {
    isLoggedRef.current = isLogged;
  }, [isLogged]);

  const [incentiveUserId, setIncentiveUserId] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);

  useEffect(() => {
    setRastrearClientId(clientId);
  }, [clientId]);

 const stringToHex = useCallback((str: string) => {
  const crypt = new JSEncrypt();

  crypt.setPublicKey(publicKeyRSA);
  const encrypted = crypt.encrypt(str);

return encrypted;
 }, []);

  const signIn = useCallback(async ({token, user}: SignInCredentials) => {
  try {
    barataoApis.forEach(item => {
      item.defaults.headers.common.Authorization = `${token}`;
    });

    let updatedUserData = user;

    try {
      const response = await api.get('/user_app');
      updatedUserData = response.data.user;
    } catch (error) {
      console.log(
        'Não foi possível buscar dados atualizados do usuário:',
        error,
      );
    }

    const USER_AVATAR =
      updatedUserData.avatarUrl !== null
        ? updatedUserData.avatarUrl
        : defaultAvatar;
    const isPhoneVerified = !!updatedUserData.phone_verified;

    const userCollection = database.get<ModelUser>('users');
    await database.write(async () => {
      await userCollection.create(newUser => {
        (newUser.user_id = updatedUserData.id),
          (newUser.name = updatedUserData.name),
          (newUser.cpf = updatedUserData.cpf),
          (newUser.email = updatedUserData.email),
          (newUser.phone = updatedUserData.phone),
          (newUser.avatarUrl = USER_AVATAR),
          (newUser.token = token);
        newUser.codCompartilhar = updatedUserData.codCompartilhar;
        newUser.phone_verified = isPhoneVerified;

        setData({
          ...updatedUserData,
          token,
          id: newUser.id,
          user_id: updatedUserData.id,
          avatarUrl: USER_AVATAR,
          phone_verified: isPhoneVerified,
        });
        setIsLogged(true);
      });
    });

    setRastrearLogado(true);

    if (Platform.OS !== 'web') {
      identifyUserForPush(updatedUserData.id);
      registerFcmToken();
    }
  } catch (error: any) {
    throw new Error(error);
  }
}, []);

  const destroyAllUsers = useCallback(async () => {
  const userCollection = database.get<ModelUser>('users');

  await database.write(async () => {
    try {
      const response = await userCollection.query().fetch();
      if (response.length > 0) {
        for (const eachUser of response) {
          await eachUser.destroyPermanently();
        }
        setData(INITIAL_USER as User);
        setIsLogged(false);

        barataoApis.forEach(item => {
          item.defaults.headers.common.Authorization = '';
        });
      }
    } catch (error) {
      console.error('Erro ao remover o usuário:', error);
    }
  });
}, []);

  const signOut = useCallback(async () => {
  if (signingOutRef.current) {
    return;
  }
  signingOutRef.current = true;
  setRastrearLogado(false);

  setShouldShowOTPModal(false);
  handleClearUserCardsList();

  //TO-DO -> PARECE QUE ESTA CHAMANDO O FIREBASE MESMO SEM ESTAR LOGADO.

  try {
    // Limpa o estado/DB local primeiro — é isso que efetivamente desloga o
    // usuário (isLogged=false). As chamadas de rede abaixo são best-effort
    // (limpeza de token de push) e não podem bloquear o logout percebido
    // pelo usuário caso a rede esteja lenta ou indisponível.
    await destroyAllUsers();

    await Promise.all([
      AsyncStorage.removeItem('creditcard_term'),
    ]);

    rastrear({status_tela: 'logout'});

    // Resetar os carrinhos para não manter dados da conta antiga
    resetCarrinhos();

    if (Platform.OS !== 'web') {
      identifyUserForPush(null);
      // Tentar deletar o token FCM sem bloquear o signOut — best-effort
      deleteFcmToken().catch(fcmError => {
        console.log(
          '[FIREBASE]: Erro ao deletar token FCM (ignorado):',
          fcmError,
        );
      });
    }
  } catch (error: any) {
    throw new Error(error);
  } finally {
    signingOutRef.current = false;
  }
}, [handleClearUserCardsList, resetCarrinhos]);

  const updateAvatar = useCallback(async (user: User) => {
  try {
    await api
      .put('/users', {
        avatarUrl: user.avatarUrl,
      })
      .then(async () => {
        const userCollection = database.get<ModelUser>('users');
        await database.write(async () => {
          const userSelected = await userCollection.find(user.id);
          await userSelected.update(userData => {
            userData.avatarUrl = user.avatarUrl;
          });
        });
        setData(user);
      })
      .catch(() => {
        throw new Error('Não foi possível atualizar o avatar.');
      });
  } catch (error: any) {
    throw new Error(error);
  }
}, []);

  const externalAuthentation = useCallback(async (
  token: string,
  incentiveUserId?: string | null,
  clientId?: string,
) => {
  try {
    barataoApis.forEach(item => {
      item.defaults.headers.common.Authorization = `${token}`;
    });

    const response = await api.get('/user_app');
    const {user} = response.data;

    const USER_AVATAR =
      user.avatarUrl !== null ? user.avatarUrl : defaultAvatar;
    const isPhoneVerified = !!user.phone_verified;
    const userCollection = database.get<ModelUser>('users');
    await database.write(async () => {
      await userCollection.create(newUser => {
        (newUser.user_id = user.id),
          (newUser.name = user.name),
          (newUser.cpf = user.cpf),
          (newUser.email = user.email),
          (newUser.phone = user.phone),
          (newUser.avatarUrl = USER_AVATAR),
          (newUser.token = token);
        newUser.codCompartilhar = user.codCompartilhar;
        newUser.phone_verified = isPhoneVerified;

        setData({
          ...user,
          token,
          id: newUser.id,
          user_id: user.id,
          avatarUrl: USER_AVATAR,
          phone_verified: isPhoneVerified,
        });

        setIncentiveUserId(incentiveUserId || null);
        setClientId(clientId || null);

        setIsLogged(true);
      });
    });
    setRastrearLogado(true);
  } catch (error) {
    console.log(error);
  }
}, []);

  const fetchUserData = useCallback(async (
  token?: string,
  incentiveUserId?: string | null,
  clientId?: string,
) => {
  if (token) {
    await destroyAllUsers();

    await externalAuthentation(token, incentiveUserId, clientId);
    setIsAuthReady(true);
  } else {
    const userCollection = database.get<ModelUser>('users');
    const userResponse = await userCollection.query().fetch();

    if (userResponse.length > 0) {
      const userData = userResponse[0]._raw as unknown as User;

      barataoApis.forEach(item => {
        item.defaults.headers.common.Authorization = `${userData.token}`;
      });

      try {
        const response = await api.get('/user_app');
        const {user} = response.data;

        const USER_AVATAR =
          user.avatarUrl !== null ? user.avatarUrl : defaultAvatar;

        await database.write(async () => {
          const existingUser = userResponse[0];
          await existingUser.update(userToUpdate => {
            userToUpdate.user_id = user.id;
            userToUpdate.name = user.name;
            userToUpdate.cpf = user.cpf;
            userToUpdate.email = user.email;
            userToUpdate.phone = user.phone;
            userToUpdate.avatarUrl = USER_AVATAR;
            userToUpdate.token = existingUser.token;
            userToUpdate.codCompartilhar = user.codCompartilhar;
            userToUpdate.phone_verified = !!user.phone_verified;
          });

          setData({
            ...user,
            token: existingUser.token,
            id: existingUser.id,
            user_id: user.id,
            phone_verified: !!user.phone_verified,
          });
        });

        setIsLogged(true);
        setRastrearLogado(true);
      } catch (error) {
        console.log(error);
      }
    }
  }
}, [destroyAllUsers, externalAuthentation]);

const loadUserData = useCallback(async () => {
  const userCollection = database.get<ModelUser>('users');
  const response = await userCollection.query().fetch();

  if (response.length > 0) {
    const userData = response[0]._raw as unknown as User;
    userData.phone_verified = !!userData.phone_verified;

    barataoApis.forEach(item => {
      item.defaults.headers.common.Authorization = `${userData.token}`;
    });

    if (!userData.avatarUrl) {
      userData.avatarUrl = defaultAvatar;
    }

    setData(userData);
    setIsLogged(true);
    setRastrearLogado(true);
  }
  setIsAuthReady(true);
}, []);

  const markPhoneAsVerified = useCallback(async () => {
    try {
      if (data.id) {
        const userCollection = database.get<ModelUser>('users');
        await database.write(async () => {
          try {
            const storedUser = await userCollection.find(data.id);
            await storedUser.update(record => {
              record.phone_verified = true;
            });
          } catch (error) {
            console.log('Erro ao atualizar phone_verified localmente:', error);
          }
        });
      }
    } catch (error) {
      console.log('Erro ao tentar marcar telefone como verificado:', error);
    } finally {
      setData(prev => ({
        ...prev,
        phone_verified: true,
      }));
    }
  }, [data.id]);

  // API para mostrar modal de SMS a cada X dias. para o usuário
  /*
  const checkPhoneVerificationStatus = useCallback(async () => {
    try {
      const response = await api.get('/phone_verifications/verifica');
      const responseData = response?.data ?? {};
      const isVerified =
        responseData.telefone_validado === true ||
        responseData.verified === true ||
        responseData.phone_verified === true ||
        responseData.ok === true ||
        responseData.valid === true;

      if (isVerified) {
        if (!data.phone_verified) {
          await markPhoneAsVerified();
        }
        setShouldShowOTPModal(false);
        return;
      }
    } catch (error) {
      // No-op: avoid auto-opening the SMS modal on settings; open only on user action or signup.
    }
  }, [data.phone_verified, markPhoneAsVerified]);
  */

  function isUnauthorizedTokenError(error: AxiosError<any>) {
    const status = error?.response?.status;
    const errorsMsg = error?.response?.data?.errors;



    return (
      status === 401 &&
      typeof errorsMsg === 'string' &&
      errorsMsg.toLowerCase().includes('token is required or invalid')
    );
  }

  function attachUnauthorizedSignOutInterceptors(instances: AxiosInstance[]) {
    const ejectors: Array<{instance: AxiosInstance; id: number}> = [];

    const handleRejected = async (error: AxiosError<any>) => {
      if (
        isUnauthorizedTokenError(error) &&
        isLoggedRef.current &&
        !signingOutRef.current
      ) {
        try {
          await signOut();
          setSessionExpired(true);
          setShowSign(true);
        } catch {}
      }
      return Promise.reject(error);
    };

    for (const inst of instances) {
      const id = inst.interceptors.response.use(
        response => response,
        handleRejected,
      );
      ejectors.push({instance: inst, id});
    }

    return () => {
      ejectors.forEach(({instance, id}) =>
        instance.interceptors.response.eject(id),
      );
    };
  }

  async function registerFcmToken() {
    try {
      const authToken = api.defaults.headers.Authorization;
      const messaging = getMessaging();
      const fcm_token = await getToken(messaging);
      const platform = Platform.OS;

      const response = await api.post('/push_token', {
        fcm_token,
        platform,
      });

      console.log('[FIREBASE]: FIREBASE CONFIGURADO:', {
        resposta: response.data,
        fcm_token: fcm_token,
        platform,
        authToken,
      });
    } catch (error) {
      console.error(
        '[FIREBASE]: PROBLEMA NA CONFIGURAÇÃO DO FIREBASE MESSAGING:',
        error,
      );
    }
  }
  async function deleteFcmToken() {
    try {
      const authToken = api.defaults.headers.Authorization;
      const platform = Platform.OS;

      const response = await api.delete('/push_token');

      console.log('[FIREBASE]: FIREBASE DESCONFIGURADO:', {
        resposta: response.data,
        fcm_token: '',
        platform,
        authToken,
      });
    } catch (error) {
      console.error(
        '[FIREBASE]: PROBLEMA NA DESCONFIGURAÇÃO DO FIREBASE MESSAGING:',
        error,
      );
    }
  }

 useEffect( () => {
    loadUserData();

    const detach = attachUnauthorizedSignOutInterceptors(barataoApis);
    return () => detach?.();
  }, [loadUserData]);

  // API para mostrar modal a cada X dias
  /*
  useEffect(() => {
    if (!isLogged) {
      hasCheckedPhoneVerificationRef.current = false;
      return;
    }

    if (hasCheckedPhoneVerificationRef.current) return;
    hasCheckedPhoneVerificationRef.current = true;
    checkPhoneVerificationStatus();
  }, [isLogged, checkPhoneVerificationStatus]);
  */

 const value = useMemo(
  () => ({
    user: data,
    isLogged,
    showSign,
    setShowSign,
    signIn,
    stringToHex,
    clientId,
    setClientId,
    incentiveUserId,
    signOut,
    updateAvatar,
    fetchUserData,
    defaultAvatar,
    setShouldShowOTPModal,
    sessionExpired,
    setSessionExpired,
    signDismissable,
    setSignDismissable,
    markPhoneAsVerified,
    isEmprestimoEligible,
    isAuthReady,
    setIsEmprestimoEligible,
    forgotPasswordFromChangePassword,
    setForgotPasswordFromChangePassword,
    openSign,
    signStatusTela,
  }),
  [
    data,
    isLogged,
    showSign,
    signIn,
    stringToHex,
    clientId,
    incentiveUserId,
    signOut,
    updateAvatar,
    fetchUserData,
    sessionExpired,
    signDismissable,
    markPhoneAsVerified,
    isEmprestimoEligible,
    isAuthReady,
    forgotPasswordFromChangePassword,
    signStatusTela,
  ],
);

return (
  <AuthContext.Provider value={value}>
    {children}
  </AuthContext.Provider>
);
}

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);
  return context;
}

export {AuthProvider, useAuth};
